import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SignatureDialog from '@/components/SignatureDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { FolderKanban, FileText, Download, PenTool, CheckCircle2, Pencil, Eye, Clock, ChevronsUpDown, AlertTriangle, KeyRound, Settings, MessageSquare, Wrench, Plus, X } from 'lucide-react';
import TemplateManager, { iconFor } from '@/components/TemplateManager';
import FileUpload from '@/components/FileUpload';
import FileViewer from '@/components/FileViewer';
import FreeTextNotice from '@/components/FreeTextNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Données par défaut d'un nouveau document, quel que soit le modèle - les
// champs propres à chaque modèle (texte, date, cases à cocher) se créent au
// fil de la saisie dans GenericFicheFields, sans initialisation préalable.
const emptyFicheData = (user) => ({
  checklist: {},
  date: new Date().toISOString().slice(0, 10),
  agent_nom: user?.name || '',
  agent_signature: null,
});

const DocTerrain = ({ user, onLogout }) => {
  const [sites, setSites] = useState([]);
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSite, setFilterSite] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [newDocSiteId, setNewDocSiteId] = useState('');
  const [savingSiteTypes, setSavingSiteTypes] = useState(null);
  const [showSiteManagement, setShowSiteManagement] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null); // null = nouveau document
  const [formSiteId, setFormSiteId] = useState('');
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [validating, setValidating] = useState(null);
  const [validatingDocId, setValidatingDocId] = useState(null);
  const [showValidationSignature, setShowValidationSignature] = useState(false);

  // Agent réellement rédacteur du document, confirmé par son code personnel -
  // même mécanisme que le rédacteur d'un événement de la main courante,
  // nécessaire uniquement à la création (pas pour consulter/modifier après coup).
  const [siteAgents, setSiteAgents] = useState([]);
  const [redacteurId, setRedacteurId] = useState('');
  const [redacteurPickerOpen, setRedacteurPickerOpen] = useState(false);
  const [redacteurPin, setRedacteurPin] = useState('');
  const [redacteurPinDialogOpen, setRedacteurPinDialogOpen] = useState(false);
  const [pinChecking, setPinChecking] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState('');

  const canWrite = user?.permission_overrides?.create_doc_terrain ?? user?.role === 'admin';
  const canValidate = user?.permission_overrides?.validate_doc_terrain ?? ['admin', 'chef_poste'].includes(user?.role);
  const canManageTemplates = user?.permission_overrides?.manage_doc_terrain_templates ?? ['admin', 'chef_poste'].includes(user?.role);

  useEffect(() => {
    loadSites();
    loadDocs();
    loadTemplates();
    // eslint-disable-next-line
  }, [filterSite, filterStatus]);

  const loadTemplates = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain-templates`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTemplates(await res.json());
    } catch { /* silencieux */ }
  };

  // Liste des agents du site sélectionné, pour désigner le rédacteur - utile
  // uniquement lors de la création d'un nouveau document.
  useEffect(() => {
    if (editingDoc || !formSiteId) { setSiteAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${formSiteId}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSiteAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
    // eslint-disable-next-line
  }, [formSiteId, editingDoc]);

  const verifyRedacteurPin = async (pin, agentId) => {
    setPinChecking(true);
    setPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setPinVerified(true);
        setRedacteurPinDialogOpen(false);
        // Le nom de l'agent rédacteur confirmé se reporte automatiquement en
        // bas de la fiche (champ "Nom de l'agent de prévention et de sécurité").
        const agentName = siteAgents.find((a) => a.id === agentId)?.name;
        if (agentName) setFormData((prev) => ({ ...prev, agent_nom: agentName }));
      } else {
        const err = await res.json();
        setPinError(err.detail || 'Code incorrect');
        setPinVerified(false);
        setRedacteurPin('');
      }
    } catch {
      setPinError('Erreur de connexion, merci de réessayer');
      setPinVerified(false);
    } finally {
      setPinChecking(false);
    }
  };

  const loadSites = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
        if (user?.role !== 'admin' && data.length > 0) {
          setFormSiteId((prev) => prev || data[0].id);
          setNewDocSiteId((prev) => prev || data[0].id);
        }
      }
    } catch { /* silencieux */ }
  };

  const loadDocs = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      if (filterSite !== 'all') params.append('site_id', filterSite);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setDocs(await res.json());
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getSiteName = (siteId) => sites.find((s) => s.id === siteId)?.name || 'Site inconnu';

  // Une fiche non cochée pour ce site dans "Gestion par site" (admin) reste
  // masquée ici - null/absent = toutes activées (comportement historique).
  const isTypeEnabledForSite = (typeKey, siteId) => {
    const site = sites.find((s) => s.id === siteId);
    if (!site || site.enabled_doc_terrain_types == null) return true;
    return site.enabled_doc_terrain_types.includes(typeKey);
  };

  // Active/désactive une fiche pour un site donné ("Gestion par site",
  // réservée aux administrateurs côté serveur).
  const handleToggleSiteDocType = async (site, typeKey, checked) => {
    const current = site.enabled_doc_terrain_types == null ? templates.map((t) => t.doc_type) : site.enabled_doc_terrain_types;
    const next = checked ? [...new Set([...current, typeKey])] : current.filter((k) => k !== typeKey);
    setSavingSiteTypes(site.id);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites/${site.id}/doc-terrain-types`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled_types: next })
      });
      if (res.ok) {
        const updated = await res.json();
        setSites((prev) => prev.map((s) => (s.id === site.id ? updated : s)));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSavingSiteTypes(null);
    }
  };

  const openNewDoc = (docType) => {
    setSelectedDocType(docType);
    setEditingDoc(null);
    setReadOnly(false);
    setFormSiteId(newDocSiteId);
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
    setFormData(emptyFicheData(user));
    setShowForm(true);
  };

  const openDoc = (doc, { edit }) => {
    setSelectedDocType(doc.doc_type);
    setEditingDoc(doc);
    setFormSiteId(doc.site_id);
    setFormData(doc.data || {});
    setReadOnly(!edit);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDoc(null);
    setSelectedDocType(null);
    setFormData({});
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formSiteId) {
      toast.warning('Veuillez sélectionner un site');
      return;
    }
    if (!editingDoc && (!redacteurId || !pinVerified)) {
      toast.warning('Sélectionnez l\'agent rédacteur et confirmez son code personnel');
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      let response;
      if (editingDoc) {
        response = await fetch(`${BACKEND_URL}/api/doc-terrain/${editingDoc.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: formData })
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/doc-terrain`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            doc_type: selectedDocType, site_id: formSiteId, data: formData,
            redacteur_id: redacteurId, redacteur_pin: redacteurPin
          })
        });
      }
      if (response.ok) {
        toast.success(editingDoc ? 'Document mis à jour' : 'Document créé');
        closeForm();
        loadDocs();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidateSign = async (signature) => {
    const docId = validatingDocId;
    setShowValidationSignature(false);
    setValidating(docId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain/${docId}/validate`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature })
      });
      if (res.ok) {
        toast.success('Document validé');
        loadDocs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la validation');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setValidating(null);
      setValidatingDocId(null);
    }
  };

  const downloadPdf = async (docId, docType) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/doc-terrain/${docId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${docType}_${docId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const typeLabel = (key) => templates.find((t) => t.doc_type === key)?.label || key;
  const currentTemplate = templates.find((t) => t.doc_type === selectedDocType) || null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 animate-fade-in" data-testid="doc-terrain">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            Doc Terrain
          </h1>
          <p className="text-gray-600 dark:text-slate-300">
            Fiches d'intervention terrain (SST, appréhension...) — générées en PDF, validées par un chef de poste.
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Archivage : chaque document est automatiquement supprimé 1 mois après sa création.
          </p>
        </div>

        {canWrite && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">Nouveau document</h2>
            <div className="space-y-1.5 mb-3 max-w-xs">
              <Label className="text-xs">Site concerné</Label>
              <Select value={newDocSiteId} onValueChange={setNewDocSiteId}>
                <SelectTrigger data-testid="new-doc-site"><SelectValue placeholder="Sélectionnez un site" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!newDocSiteId ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">Choisissez un site pour voir les fiches disponibles.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.filter((t) => isTypeEnabledForSite(t.doc_type, newDocSiteId)).map((t) => {
                  const Icon = iconFor(t.icon);
                  return (
                    <Card key={t.doc_type} className="card-hover cursor-pointer" onClick={() => openNewDoc(t.doc_type)} data-testid={`new-doc-${t.doc_type}`}>
                      <CardHeader className="p-4">
                        <div className={`w-9 h-9 ${t.color || 'bg-blue-700'} rounded-lg flex items-center justify-center mb-2`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <CardTitle>{t.label}</CardTitle>
                        <CardDescription>{t.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  );
                })}
                {templates.every((t) => !isTypeEnabledForSite(t.doc_type, newDocSiteId)) && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 col-span-full">
                    Aucune fiche activée pour ce site (voir "Gestion par site" ci-dessous).
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {user?.role === 'admin' && (
            <Button variant="outline" size="sm" onClick={() => setShowSiteManagement(true)} data-testid="open-site-management-btn">
              <Settings className="h-4 w-4 mr-2" />
              Gestion par site
            </Button>
          )}
          {canManageTemplates && (
            <Button variant="outline" size="sm" onClick={() => setShowTemplateManager(true)} data-testid="open-template-manager-btn">
              <Wrench className="h-4 w-4 mr-2" />
              Gérer les modèles de fiche
            </Button>
          )}
        </div>

        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="text-base">Filtrer</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Site</Label>
              <Select value={filterSite} onValueChange={setFilterSite}>
                <SelectTrigger><SelectValue placeholder="Tous les sites" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Statut</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="valide">Validé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : docs.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="py-12 text-center">
                <FolderKanban className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-slate-300">Aucun document Doc Terrain pour le moment</p>
              </CardContent>
            </Card>
          ) : (
            docs.map((doc) => (
              <Card key={doc.id} className="glass-effect" data-testid={`doc-terrain-item-${doc.id}`}>
                <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline">{typeLabel(doc.doc_type)}</Badge>
                      {doc.status === 'valide' ? (
                        <Badge className="bg-green-600 text-white flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Validé
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500 text-white flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Brouillon
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{getSiteName(doc.site_id)}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Créé par {doc.created_by_name} — {new Date(doc.created_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                      {doc.status === 'valide' && doc.validated_by_name && ` · Validé par ${doc.validated_by_name}`}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openDoc(doc, { edit: false })} data-testid={`view-doc-${doc.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Voir
                    </Button>
                    {doc.status !== 'valide' && canWrite && (
                      <Button variant="outline" size="sm" onClick={() => openDoc(doc, { edit: true })} data-testid={`edit-doc-${doc.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Modifier
                      </Button>
                    )}
                    {doc.status !== 'valide' && canValidate && (
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={() => { setValidatingDocId(doc.id); setShowValidationSignature(true); }}
                        disabled={validating === doc.id}
                        data-testid={`validate-doc-${doc.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Valider
                      </Button>
                    )}
                    {doc.pdf_file_id && (
                      <Button variant="outline" size="sm" onClick={() => downloadPdf(doc.id, doc.doc_type)} data-testid={`download-doc-${doc.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* ============ FORMULAIRE (création / consultation / modification) ============ */}
        <Dialog open={showForm} onOpenChange={(open) => !open && closeForm()}>
          <DialogContent className="max-w-3xl md:max-w-4xl lg:max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {editingDoc ? (readOnly ? 'Consulter' : 'Modifier') : 'Nouveau'} — {typeLabel(selectedDocType)}
              </DialogTitle>
            </DialogHeader>

            {editingDoc?.status === 'valide' && (
              <p className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-md p-2">
                Ce document est validé et ne peut plus être modifié.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Site *</Label>
                <Select value={formSiteId} onValueChange={setFormSiteId} disabled={!!editingDoc || readOnly}>
                  <SelectTrigger data-testid="doc-terrain-site"><SelectValue placeholder="Sélectionnez un site" /></SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {!editingDoc && (
                <div className="space-y-2">
                  <Label>Agent rédacteur *</Label>
                  <Popover open={redacteurPickerOpen} onOpenChange={setRedacteurPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        disabled={!formSiteId}
                        className="w-full justify-between font-normal"
                        data-testid="doc-terrain-redacteur-picker-trigger"
                      >
                        {siteAgents.find((a) => a.id === redacteurId)?.name || (formSiteId ? 'Sélectionnez un agent' : 'Choisissez d\'abord un site')}
                        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0">
                      <Command>
                        <CommandInput placeholder="Rechercher un agent..." />
                        <CommandList>
                          <CommandEmpty>Aucun agent trouvé pour ce site.</CommandEmpty>
                          <CommandGroup>
                            {siteAgents.map((a) => (
                              <CommandItem
                                key={a.id}
                                value={a.name}
                                onSelect={() => {
                                  setRedacteurId(a.id);
                                  setRedacteurPin('');
                                  setPinVerified(false);
                                  setPinError('');
                                  setRedacteurPickerOpen(false);
                                  setRedacteurPinDialogOpen(true);
                                }}
                              >
                                {a.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Permet de savoir qui a réellement rédigé cette fiche.</p>

                  {redacteurId && (
                    <div className="pt-1">
                      {pinVerified ? (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span>Code personnel confirmé</span>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => { setRedacteurPin(''); setPinVerified(false); setPinError(''); setRedacteurPinDialogOpen(true); }}
                            data-testid="doc-terrain-redacteur-pin-edit"
                          >
                            Modifier
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setRedacteurPinDialogOpen(true)}
                          data-testid="doc-terrain-redacteur-pin-open-btn"
                        >
                          <KeyRound className="h-4 w-4" />
                          Saisir le code personnel de l'agent *
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentTemplate && (
                <GenericFicheFields
                  template={currentTemplate}
                  data={formData}
                  setData={setFormData}
                  readOnly={readOnly}
                  onOpenSignature={() => setShowSignature(true)}
                />
              )}

              {!readOnly && (
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="btn-primary flex-1" disabled={submitting}>
                    {submitting ? 'Enregistrement...' : editingDoc ? 'Enregistrer les modifications' : 'Créer le document'}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeForm}>Annuler</Button>
                </div>
              )}
            </form>
          </DialogContent>
        </Dialog>

        {/* Popup de saisie du code personnel du rédacteur : dès les 4 chiffres
            renseignés, le code est vérifié immédiatement auprès du serveur -
            elle ne se ferme que si le code est le bon. */}
        <Dialog open={redacteurPinDialogOpen} onOpenChange={(open) => { if (!pinChecking) setRedacteurPinDialogOpen(open); }}>
          <DialogContent className="max-w-xs">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <KeyRound className="h-4 w-4 text-indigo-600" />
                Code personnel
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Demandé à {siteAgents.find((a) => a.id === redacteurId)?.name || 'l\'agent sélectionné'} pour confirmer son identité.
            </p>
            <div className="flex flex-col items-center gap-2 py-2">
              <InputOTP
                maxLength={4}
                inputMode="numeric"
                value={redacteurPin}
                onChange={(value) => { setRedacteurPin(value); setPinError(''); }}
                onComplete={(value) => verifyRedacteurPin(value, redacteurId)}
                disabled={pinChecking}
                data-testid="doc-terrain-redacteur-pin-otp"
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} mask />
                  <InputOTPSlot index={1} mask />
                  <InputOTPSlot index={2} mask />
                  <InputOTPSlot index={3} mask />
                </InputOTPGroup>
              </InputOTP>

              {pinChecking && (
                <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
              )}

              {pinError && (
                <div
                  className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                  data-testid="doc-terrain-redacteur-pin-error"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{pinError}</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <SignatureDialog
          open={showSignature}
          onClose={() => setShowSignature(false)}
          onSign={(sig) => { setFormData((prev) => ({ ...prev, agent_signature: sig })); setShowSignature(false); }}
          title="Signature de l'agent"
        />

        {/* Signature du chef de poste/admin, obligatoire pour valider la fiche
            (voir handleValidateSign) - son nom et sa signature sont ajoutés au PDF. */}
        <SignatureDialog
          open={showValidationSignature}
          onClose={() => { setShowValidationSignature(false); setValidatingDocId(null); }}
          onSign={handleValidateSign}
          title="Signature du chef de poste"
        />

        {/* Gestion par site (admin uniquement) : dans une fenêtre à part pour
            ne pas encombrer la page principale en permanence. */}
        <Dialog open={showSiteManagement} onOpenChange={setShowSiteManagement}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gestion par site</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 dark:text-slate-400">Cochez les fiches disponibles pour chaque site.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 border-b border-slate-200 dark:border-slate-700">Site</th>
                    {templates.map((t) => (
                      <th key={t.doc_type} className="text-center p-2 border-b border-slate-200 dark:border-slate-700 font-medium text-xs">
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr key={s.id} data-testid={`site-doc-types-row-${s.id}`}>
                      <td className="p-2 border-b border-slate-100 dark:border-slate-800">{s.name}</td>
                      {templates.map((t) => (
                        <td key={t.doc_type} className="text-center p-2 border-b border-slate-100 dark:border-slate-800">
                          <Checkbox
                            checked={isTypeEnabledForSite(t.doc_type, s.id)}
                            disabled={savingSiteTypes === s.id}
                            onCheckedChange={(checked) => handleToggleSiteDocType(s, t.doc_type, checked)}
                            data-testid={`site-doc-type-${s.id}-${t.doc_type}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>

        <TemplateManager
          open={showTemplateManager}
          onOpenChange={setShowTemplateManager}
          templates={templates}
          onReload={loadTemplates}
        />
      </div>
    </Layout>
  );
};

// Rendu générique d'une fiche à partir de son modèle (template.blocks) - un
// bloc "subtitle" ouvre une nouvelle FormSection, les blocs suivants (texte,
// case à cocher, champ...) s'y accumulent jusqu'au prochain subtitle/title.
// Les cases "checkbox_obs" réutilisent le motif bouton + popup d'observation
// déjà validé pour l'ancienne fiche APS (voir ObservationButton).
const GenericFicheFields = ({ template, data, setData, readOnly, onOpenSignature }) => {
  const checklist = data.checklist || {};
  const [obsDialogKey, setObsDialogKey] = useState(null);
  const [obsDialogText, setObsDialogText] = useState('');

  const setItemOk = (key) => (checked) => setData((prev) => ({
    ...prev,
    checklist: { ...prev.checklist, [key]: { ...(prev.checklist?.[key] || {}), ok: checked } }
  }));
  const setItemObsValue = (key, value) => setData((prev) => ({
    ...prev,
    checklist: { ...prev.checklist, [key]: { ...(prev.checklist?.[key] || {}), obs: value } }
  }));
  const setField = (key) => (e) => setData((prev) => ({ ...prev, [key]: e.target.value }));

  const openObsDialog = (key) => {
    setObsDialogKey(key);
    setObsDialogText(checklist[key]?.obs || '');
  };
  const saveObsDialog = () => {
    setItemObsValue(obsDialogKey, obsDialogText.trim());
    setObsDialogKey(null);
  };

  const ObservationButton = ({ itemKey }) => {
    const hasObs = !!checklist[itemKey]?.obs;
    if (readOnly && !hasObs) return null;
    return (
      <Button
        type="button"
        variant={hasObs ? 'secondary' : 'outline'}
        size="sm"
        onClick={() => openObsDialog(itemKey)}
        className="h-7 text-xs gap-1.5"
        data-testid={`obs-button-${itemKey}`}
      >
        <MessageSquare className="h-3 w-3" />
        {hasObs ? 'Observation ajoutée' : 'Ajouter une observation'}
      </Button>
    );
  };

  // Remplace la case à cocher brute par deux boutons (vert/rouge) - plus
  // lisible sur une check-liste terrain qu'une simple coche, et sans
  // ambiguïté sur ce que "coché" veut dire. "labels" permet de réutiliser le
  // même composant pour "Conforme/Non conforme" (checkbox/checkbox_obs) et
  // "Oui/Non" (yesno), qui partagent le même stockage (checklist[key].ok).
  const ConformiteToggle = ({ itemKey, labels = ['Conforme', 'Non conforme'] }) => {
    const value = checklist[itemKey]?.ok;
    const onChange = setItemOk(itemKey);
    return (
      <div className="flex gap-1.5 shrink-0">
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onChange(true)}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            value === true
              ? 'bg-green-600 border-green-600 text-white'
              : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-green-50 dark:hover:bg-green-950/30'
          }`}
          data-testid={`conforme-${itemKey}`}
        >
          {labels[0]}
        </button>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onChange(false)}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            value === false
              ? 'bg-red-600 border-red-600 text-white'
              : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/30'
          }`}
          data-testid={`non-conforme-${itemKey}`}
        >
          {labels[1]}
        </button>
      </div>
    );
  };

  // Regroupe les blocs sous leur dernier sous-titre pour les afficher dans
  // des FormSection successives (un titre démarre un groupe à part).
  const groups = [];
  let current = null;
  (template.blocks || []).forEach((block) => {
    if (block.type === 'title') {
      groups.push({ kind: 'title', block });
      current = null;
    } else if (block.type === 'subtitle') {
      current = { kind: 'section', title: block.label, items: [] };
      groups.push(current);
    } else {
      if (!current) {
        current = { kind: 'section', title: null, items: [] };
        groups.push(current);
      }
      current.items.push(block);
    }
  });

  const renderBlock = (block) => {
    switch (block.type) {
      case 'text':
        return <p key={block.id} className="text-xs text-slate-500 dark:text-slate-400 italic">{block.label}</p>;
      case 'checkbox':
        return (
          <div key={block.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <span className="text-sm flex-1 min-w-0">{block.label}<RequiredMark block={block} /></span>
            <ConformiteToggle itemKey={block.key} />
          </div>
        );
      case 'checkbox_obs':
        return (
          <div key={block.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <span className="text-sm flex-1 min-w-0">{block.label}<RequiredMark block={block} /></span>
            <div className="flex items-center gap-1.5 shrink-0">
              <ConformiteToggle itemKey={block.key} />
              <ObservationButton itemKey={block.key} />
            </div>
          </div>
        );
      case 'yesno':
        return (
          <div key={block.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <span className="text-sm flex-1 min-w-0">{block.label}<RequiredMark block={block} /></span>
            <ConformiteToggle itemKey={block.key} labels={['Oui', 'Non']} />
          </div>
        );
      case 'observation':
        return (
          <div key={block.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <span className="text-sm flex-1 min-w-0">{block.label}<RequiredMark block={block} /></span>
            <ObservationButton itemKey={block.key} />
          </div>
        );
      case 'text_field':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <Input value={data[block.key] || ''} onChange={setField(block.key)} disabled={readOnly} />
          </Field>
        );
      case 'number_field':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <Input type="number" value={data[block.key] ?? ''} onChange={setField(block.key)} disabled={readOnly} />
          </Field>
        );
      case 'date_field':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <Input type="date" value={data[block.key] || ''} onChange={setField(block.key)} disabled={readOnly} />
          </Field>
        );
      case 'textarea_field':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <Textarea rows={3} value={data[block.key] || ''} onChange={setField(block.key)} disabled={readOnly} />
          </Field>
        );
      case 'select':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <Select
              value={data[block.key] || undefined}
              onValueChange={(v) => setData((prev) => ({ ...prev, [block.key]: v }))}
              disabled={readOnly}
            >
              <SelectTrigger data-testid={`select-${block.key}`}><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
              <SelectContent>
                {(block.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        );
      case 'photo':
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            {data[block.key] ? (
              <FileViewer
                fileIds={[data[block.key]]}
                canDelete={!readOnly}
                onDelete={async () => { setData((prev) => ({ ...prev, [block.key]: null })); return true; }}
              />
            ) : (
              !readOnly && (
                <FileUpload
                  existingFiles={[]}
                  onFilesUploaded={(ids) => setData((prev) => ({ ...prev, [block.key]: ids[ids.length - 1] }))}
                />
              )
            )}
          </Field>
        );
      case 'table': {
        const columns = block.columns || [];
        const totalWidth = columns.reduce((sum, c) => sum + (c.width || 1), 0) || 1;
        const stored = Array.isArray(data[block.key]) ? data[block.key] : [];
        const rows = stored.length > 0 ? stored : [columns.map(() => '')];
        const saveRows = (next) => setData((prev) => ({ ...prev, [block.key]: next }));
        const setCell = (rIdx, cIdx, value) => saveRows(rows.map((row, i) => (
          i === rIdx ? columns.map((_, j) => (j === cIdx ? value : (row[j] ?? ''))) : row
        )));
        return (
          <Field key={block.id} label={block.label} required={block.required}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse table-fixed min-w-[480px]" data-testid={`table-${block.key}`}>
                <thead>
                  <tr>
                    {columns.map((c, cIdx) => (
                      <th key={cIdx} style={{ width: `${((c.width || 1) / totalWidth) * 100}%` }} className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {c.name}
                      </th>
                    ))}
                    {!readOnly && <th className="w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      {columns.map((_, cIdx) => (
                        <td key={cIdx} className="border border-slate-300 dark:border-slate-600 p-0">
                          <Input
                            value={row[cIdx] ?? ''}
                            onChange={(e) => setCell(rIdx, cIdx, e.target.value)}
                            disabled={readOnly}
                            className="h-9 rounded-none border-0 shadow-none text-sm px-2 focus-visible:ring-1"
                            data-testid={`table-${block.key}-${rIdx}-${cIdx}`}
                          />
                        </td>
                      ))}
                      {!readOnly && (
                        <td className="pl-1">
                          <button type="button" onClick={() => saveRows(rows.filter((_, i) => i !== rIdx))} disabled={rows.length <= 1} className="text-slate-300 hover:text-red-600 disabled:opacity-20" title="Supprimer la ligne">
                            <X className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => saveRows([...rows, columns.map(() => '')])} data-testid={`table-${block.key}-add-row`}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une ligne
              </Button>
            )}
          </Field>
        );
      }
      case 'signature':
        return (
          <div key={block.id} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <Field label="Nom de l'agent"><Input value={data.agent_nom || ''} onChange={setField('agent_nom')} disabled={readOnly} /></Field>
            <Field label="Date"><Input type="date" value={data.date || ''} onChange={setField('date')} disabled={readOnly} /></Field>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-600 dark:text-slate-300">Signature</Label>
              {data.agent_signature ? (
                <div className="flex items-center gap-2">
                  <img src={data.agent_signature} alt="Signature" className="h-14 border rounded bg-white" />
                  {!readOnly && (
                    <Button type="button" variant="outline" size="sm" onClick={onOpenSignature}>Modifier</Button>
                  )}
                </div>
              ) : (
                !readOnly && (
                  <Button type="button" variant="outline" size="sm" onClick={onOpenSignature}>
                    <PenTool className="h-3.5 w-3.5 mr-1" /> Signer
                  </Button>
                )
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <FreeTextNotice />
      {groups.map((g, idx) => (
        g.kind === 'title' ? (
          <h2 key={g.block.id} className="text-base font-bold text-slate-800 dark:text-slate-100 pt-1">{g.block.label}</h2>
        ) : (
          <FormSection key={idx} title={g.title}>
            <div className="space-y-1">{g.items.map(renderBlock)}</div>
          </FormSection>
        )
      ))}

      {/* Popup de rédaction d'une observation - ouverte via ObservationButton,
          pré-remplie si l'item a déjà une observation (pour la modifier). */}
      <Dialog open={!!obsDialogKey} onOpenChange={(open) => !open && setObsDialogKey(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Observation</DialogTitle>
          </DialogHeader>
          <Textarea
            rows={8}
            autoFocus={!readOnly}
            placeholder="Rédigez votre observation..."
            value={obsDialogText}
            onChange={(e) => setObsDialogText(e.target.value)}
            disabled={readOnly}
            className="text-sm"
          />
          <div className="flex gap-2 justify-end">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={() => setObsDialogKey(null)}>Fermer</Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => setObsDialogKey(null)}>Annuler</Button>
                <Button type="button" className="btn-primary" onClick={saveObsDialog}>Enregistrer</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FormSection = ({ title, children }) => (
  <div className="space-y-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
    {title && <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>}
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-gray-600 dark:text-slate-300">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

// Petit rappel visuel qu'un point de contrôle/observation est obligatoire
// avant validation (voir missing_required_doc_terrain_fields côté serveur) -
// les blocs de type Field (texte, date, photo...) l'affichent via leur
// propre prop "required" ; ceux rendus en ligne (checkbox, yesno,
// observation) l'ajoutent directement après leur libellé.
const RequiredMark = ({ block }) => (block?.required ? <span className="text-red-500"> *</span> : null);

export default DocTerrain;
